/**
 * Burrs, the DOM half.  The geometry -- burrList, burrSnare, the SNAG_BOB
 * cycle -- lives in packages/engine/src/burrs.js, the one module both ends
 * import; that file's header carries the design.  This half is only the
 * on-screen cue, split out by WS-06 under §1.2's standing rule: a module
 * both halves need is an engine module, and the DOM half is its own file.
 */

/**
 * The "you're stuck!" cue: #burr in index.html, a toast in the #charge slot
 * at the bottom-centre of the screen where the pounce meter lives, because
 * the player's eyes are on their cat and the two can never show at once (a
 * tangled cat cannot gather a pounce).
 *
 * Constructed with the local player and a getter for the (initially null)
 * net, chat.js's pattern: everything it needs that may not exist yet is
 * fetched at call time.  It runs its own little rAF loop rather than asking
 * main.js's frame loop for a hook -- the loop toggles a class and nothing
 * else, and keeping it here keeps main.js's wiring to one block.
 */
export class BurrCue {
  constructor(el, { player, getNet } = {}) {
    this.el = el;
    this.player = player;
    this.getNet = getNet ?? (() => null);
    this.wired = false;
    this.stingUntil = 0;
    if (!el) return;                 // a stale page loses the cue, not the game
    const tick = () => {
      requestAnimationFrame(tick);
      this.update();
    };
    requestAnimationFrame(tick);
  }

  /**
   * The server said the sting landed on OUR cat.  The local tangle is
   * already showing (the same geometry snagged the local player a frame
   * ago), so this only flips the toast to its "that cost you" colour --
   * but it also SHOWS the toast if the local sim somehow missed the snag
   * (a seat taken mid-bush, a desync): the meter is about to dip, and a
   * penalty with no cue on screen is the unfairness this feature is built
   * to never commit.
   */
  stung() {
    this.stingUntil = performance.now() + 1400;
  }

  update() {
    if (!this.el) return;
    // Lazily attach the burr broadcast once the net exists; `on` keeps a
    // Set per tag, so this is one handler forever, not one per frame.
    const net = this.getNet();
    if (!this.wired && net) {
      this.wired = true;
      net.client.on("burr", (msg) => {
        if (msg.slot === net.slot) this.stung();
      });
    }
    const stung = performance.now() < this.stingUntil;
    this.el.classList.toggle("show", this.player.snagFor > 0 || stung);
    this.el.classList.toggle("sting", stung);
  }
}
